import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedOverlayPositionChange} from '@angular/cdk/overlay';
import {
  forwardRef,
  AfterViewInit,
  Component,
  ContentChildren,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChange,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {JdOptionContainerComponent} from './jd-option-container.component';
import {JdOptionGroupComponent} from './jd-option-group.component';
import {JdOptionComponent} from './jd-option.component';
import {defaultFilterOption, TFilterOption} from './jd-option.pipe';
import {JdSelectTopControlComponent} from './jd-select-top-control.component';
import {isNotNil, toBoolean} from '../core/convert';
import {DOWN_ARROW, SPACE, TAB} from '@angular/cdk/keycodes';
import {Subject} from "rxjs";
import {isString} from 'lodash';
@Component({
  selector: 'jd-select',
  preserveWhitespaces: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JdSelectComponent),
      multi: true
    }
  ],
  animations: [
    trigger('dropDownAnimation', [
      state('hidden', style({
        opacity: 0,
        display: 'none'
      })),
      state('bottom', style({
        opacity: 1,
        transform: 'scaleY(1)',
        transformOrigin: '0% 0%'
      })),
      state('top', style({
        opacity: 1,
        transform: 'scaleY(1)',
        transformOrigin: '0% 100%'
      })),
      transition('hidden => bottom', [
        style({
          opacity: 0,
          transform: 'scaleY(0.8)',
          transformOrigin: '0% 0%'
        }),
        animate('100ms cubic-bezier(0.755, 0.05, 0.855, 0.06)')
      ]),
      transition('bottom => hidden', [
        animate('100ms cubic-bezier(0.755, 0.05, 0.855, 0.06)', style({
          opacity: 0,
          transform: 'scaleY(0.8)',
          transformOrigin: '0% 0%'
        }))
      ]),
      transition('hidden => top', [
        style({
          opacity: 0,
          transform: 'scaleY(0.8)',
          transformOrigin: '0% 100%'
        }),
        animate('100ms cubic-bezier(0.755, 0.05, 0.855, 0.06)')
      ]),
      transition('top => hidden', [
        animate('100ms cubic-bezier(0.755, 0.05, 0.855, 0.06)', style({
          opacity: 0,
          transform: 'scaleY(0.8)',
          transformOrigin: '0% 100%'
        }))
      ])
    ])
  ],
  templateUrl: './jd-select.component.html',
  host: {
    '[class.ant-select]': 'true',
    '[class.ant-select-lg]': 'jdSize==="large"',
    '[class.ant-select-sm]': 'jdSize==="small"',
    '[class.ant-select-enabled]': '!jdDisabled',
    '[class.ant-select-disabled]': 'jdDisabled',
    '[class.ant-select-allow-clear]': 'jdAllowClear',
    '[class.ant-select-open]': 'jdOpen'
  },
  styles: [`
    .ant-select-dropdown {
      top: 100%;
      left: 0;
      position: relative;
      width: 100%;
      margin-top: 4px;
      margin-bottom: 4px;
    }
  `]
})
export class JdSelectComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
  private _disabled = false;
  private _allowClear = false;
  private _showSearch = false;
  private _open = false;
  private _placeholder: string;
  private _autoFocus = false;
  private _dropdownClassName: string;
  private _col = 1;

  dropDownPosition: 'top' | 'center' | 'bottom' = 'bottom';
  // tslint:disable-next-line:no-any
  listOfSelectedValue: any[] = [];
  listOfTemplateOption: JdOptionComponent[] = [];
  // tslint:disable-next-line:no-any
  value: any | any[];
  overlayWidth: number;
  @Input() overlayMinWidth: number;
  @Input() isLoading = false;

  @Input() displayLabel: 'jdLabel' | 'jdValue' = 'jdLabel';

  @Input() set jdOptionCol(value: number) {
    const _allowCols = {1: 24, 2: 12, 3: 8, 4: 6, 6: 4, 8: 3, 12: 2, 24: 1};
    this._col = _allowCols[value] ? _allowCols[value] : _allowCols[1];
  }

  notSearchResult$ = new Subject<{isNeedLoading: boolean, value: string}>();

  @Output() OnNotSearchResult = new EventEmitter<{isNeedLoading: boolean, value: string}>();
  get jdOptionCol() {
    return this._col;
  }

  searchValue = '';
  isDestroy = true;
  isInit = false;
  dropDownClassMap;
  @ViewChild(CdkOverlayOrigin) cdkOverlayOrigin: CdkOverlayOrigin;
  @ViewChild(CdkConnectedOverlay) cdkConnectedOverlay: CdkConnectedOverlay;
  @ViewChild(JdSelectTopControlComponent) jdSelectTopControlComponent: JdSelectTopControlComponent;
  @ViewChild(JdOptionContainerComponent) jdOptionContainerComponent: JdOptionContainerComponent;
  /** should move to jd-option-container when https://github.com/angular/angular/issues/20810 resolved **/
  @ContentChildren(JdOptionComponent) listOfJdOptionComponent: QueryList<JdOptionComponent>;
  @ContentChildren(JdOptionGroupComponent) listOfJdOptionGroupComponent: QueryList<JdOptionGroupComponent>;
  @Output() jdOnSearch = new EventEmitter<string>();
  @Output() jdScrollToBottom = new EventEmitter<void>();
  @Output() jdOpenChange = new EventEmitter<boolean>();
  @Input() jdSize = 'default';
  @Input() jdServerSearch = false;
  @Input() jdMode: 'default' | 'multiple' | 'tags' = 'default';
  @Input() jdDropdownMatchSelectWidth = true;
  @Input() jdFilterOption: TFilterOption = defaultFilterOption;
  @Input() jdMaxMultipleCount = Infinity;
  @Input() jdDropdownStyle: { [key: string]: string; };
  @Input() jdNotFoundContent: string;
  /** https://github.com/angular/angular/pull/13349/files **/
    // tslint:disable-next-line:no-any
  @Input() compareWith = (o1: any, o2: any) => {
    if (isString(o1) && (o1 as string).indexOf('~') > -1) {
      o1 = (o1 as string).split('~')[0];
    }
    if (isString(o2) && (o2 as string).indexOf('~') > -1) {
      o2 = (o2 as string).split('~')[0];
    }
    return o1 === o2;
  };

  @Input()
  set jdDropdownClassName(value: string) {
    this._dropdownClassName = value;
    this.updateDropDownClassMap();
  }

  get jdDropdownClassName(): string {
    return this._dropdownClassName;
  }

  @Input()
  set jdAutoFocus(value: boolean) {
    this._autoFocus = toBoolean(value);
    this.updateAutoFocus();
  }

  get jdAutoFocus(): boolean {
    return this._autoFocus;
  }

  @Input()
  set jdOpen(value: boolean) {
    this._open = value;
    this.handleEscBug();
    this.updateCdkConnectedOverlayStatus();
    this.updateDropDownClassMap();
    this.jdOpenChange.emit(value);
    if (this.jdOpen) {
      if (this.jdSelectTopControlComponent) {
        this.jdSelectTopControlComponent.focusOnInput();
        this.jdSelectTopControlComponent.setInputValue('', true);
      }
      if (this.jdOptionContainerComponent) {
        this.jdOptionContainerComponent.scrollIntoView();
      }
      if (this.cdkConnectedOverlay && this.cdkConnectedOverlay.overlayRef) {
        this.cdkConnectedOverlay.overlayRef.updatePosition();
        const backdropElement = this.cdkConnectedOverlay.overlayRef.backdropElement;
        const parentNode = this.renderer.parentNode(backdropElement);
        const hostElement = this.cdkConnectedOverlay.overlayRef.hostElement;
        this.renderer.appendChild(parentNode, backdropElement);
        this.renderer.appendChild(parentNode, hostElement);
      }
    } else {
      if (this.jdSelectTopControlComponent) {
        this.jdSelectTopControlComponent.setInputValue('', false);
      }
      if (this.jdOptionContainerComponent) {
        this.jdOptionContainerComponent.resetActiveOption();
      }
    }
  }

  get jdOpen(): boolean {
    return this._open;
  }

  @Input()
  set jdDisabled(value: boolean) {
    this._disabled = toBoolean(value);
    if (this.jdDisabled) {
      this.closeDropDown();
    }
  }

  get jdDisabled(): boolean {
    return this._disabled;
  }

  @Input()
  set jdAllowClear(value: boolean) {
    this._allowClear = toBoolean(value);
  }

  get jdAllowClear(): boolean {
    return this._allowClear;
  }

  @Input()
  set jdShowSearch(value: boolean) {
    this._showSearch = toBoolean(value);
  }

  get jdShowSearch(): boolean {
    return this._showSearch;
  }

  @Input()
  set jdPlaceHolder(value: string) {
    this._placeholder = value;
  }

  get jdPlaceHolder(): string {
    return this._placeholder;
  }

  onChange: (value: string | string[]) => void = () => null;
  onTouched: () => void = () => null;

  @HostListener('click')
  onClick(): void {
    if (!this.jdDisabled) {
      this.jdOpen = !this.jdOpen;
      // this.jdOpenChange.emit(this.jdOpen);
    }
  }

  @HostListener('keydown', ['$event'])
  _handleKeydown(event: KeyboardEvent): void {
    if (this._disabled) {
      return;
    }
    const keyCode = event.keyCode;
    if (!this._open) {
      if (keyCode === SPACE || keyCode === DOWN_ARROW) {
        this.jdOpen = true;
        this.jdOpenChange.emit(this.jdOpen);
        event.preventDefault();
      }
    } else {
      if (keyCode === SPACE || keyCode === TAB) {
        this.jdOpen = false;
        this.jdOpenChange.emit(this.jdOpen);
        event.preventDefault();
      }
    }
  }

  @HostListener('keyup.enter', ['$event'])
  onEnter($event: KeyboardEvent) {
    /*if (!this.jdDisabled) {
      if (this.jdOpen) {
        this.jdOpen = false;
      } else {
        this.jdOpen = true;
      }
      this.jdOpenChange.emit(this.jdOpen);
    }
    console.log(this.jdOpen);*/
  }

  updateAutoFocus(): void {
    if (this.isInit && this.jdSelectTopControlComponent.inputElement) {
      if (this.jdAutoFocus) {
        this.renderer.setAttribute(this.jdSelectTopControlComponent.inputElement.nativeElement, 'autofocus', 'autofocus');
      } else {
        this.renderer.removeAttribute(this.jdSelectTopControlComponent.inputElement.nativeElement, 'autofocus');
      }
    }
  }

  focus(): void {
    if (this.jdSelectTopControlComponent.inputElement) {
      this.jdSelectTopControlComponent.inputElement.nativeElement.focus();
    }
  }

  blur(): void {
    if (this.jdSelectTopControlComponent.inputElement) {
      this.jdSelectTopControlComponent.inputElement.nativeElement.blur();
    }
    this.jdOpen = false;
  }

  /** overlay can not be always open , reopen overlay after press esc **/
  handleEscBug(): void {
    if (this.jdOpen && this.cdkConnectedOverlay && this.cdkConnectedOverlay.overlayRef && !this.cdkConnectedOverlay.overlayRef.backdropElement) {
      this.cdkConnectedOverlay.open = true;
      this.cdkConnectedOverlay.ngOnChanges({open: new SimpleChange(false, true, false)});
    }
  }

  onKeyDownCdkOverlayOrigin(e: KeyboardEvent): void {
    if (this.jdOptionContainerComponent) {
      if (!this.jdOpen && e.keyCode === 13) {
        setTimeout(() => this.jdOpen = true, 10);
      }
      this.jdOptionContainerComponent.onKeyDownUl(e);
    }
  }

  closeDropDown(): void {
    if (this.jdOpen) {
      this.onTouched();
      this.jdOpen = false;
      // this.jdOpenChange.emit(this.jdOpen);
      // this.blur();
    }
  }

  onPositionChange(position: ConnectedOverlayPositionChange): void {
    this.dropDownPosition = position.connectionPair.originY;
    this.updateDropDownClassMap();
  }

  onClickOptionFromOptionContainer(): void {
    if (this.isSingleMode) {
      this.closeDropDown();
    } else if (this.jdMode === 'tags') {
      this.onSearch('', true);
    }
  }

  updateCdkConnectedOverlayStatus(): void {
    if (this.isInit && this.jdOpen && this.cdkOverlayOrigin) {
      if (this.jdDropdownMatchSelectWidth) {
        this.overlayWidth = this.cdkOverlayOrigin.elementRef.nativeElement.getBoundingClientRect().width;
        this.cdkConnectedOverlay.overlayRef.updateSize({width: this.overlayWidth});
      } else {
        this.overlayMinWidth = this.cdkOverlayOrigin.elementRef.nativeElement.getBoundingClientRect().width;
        this.cdkConnectedOverlay.overlayRef.updateSize({minWidth: this.overlayMinWidth});
      }

    }
    this.updateCdkConnectedOverlayPositions();
    if (this.cdkConnectedOverlay && this.cdkConnectedOverlay.overlayRef && this.cdkConnectedOverlay.overlayRef.backdropElement) {
      if (this.jdOpen) {
        this.renderer.removeStyle(this.cdkConnectedOverlay.overlayRef.backdropElement, 'display');
      } else {
        this.renderer.setStyle(this.cdkConnectedOverlay.overlayRef.backdropElement, 'display', 'none');
      }
    }
  }

  updateCdkConnectedOverlayPositions(): void {
    /** wait for input size change **/
    setTimeout(() => this.cdkConnectedOverlay.overlayRef.updatePosition(), 160);
  }

  get isSingleMode(): boolean {
    return this.jdMode === 'default';
  }

  get isMultipleOrTags(): boolean {
    return this.jdMode === 'tags' || this.jdMode === 'multiple';
  }

  /** option container jdListOfSelectedValueChange -> update ngModel **/
  // tslint:disable-next-line:no-any
  updateListOfSelectedValueFromOptionContainer(value: any[]): void {
    this.clearSearchValue();
    this.updateFromSelectedList(value);
  }

  /** option container jdListOfSelectedValueChange -> update ngModel **/
  // tslint:disable-next-line:no-any
  updateListOfSelectedValueFromTopControl(value: any[]): void {
    this.clearSearchValue();
    this.updateFromSelectedList(value);
  }

  // tslint:disable-next-line:no-any
  updateFromSelectedList(value: any[]): void {
    let modelValue;
    if (this.isSingleMode) {
      if (value.length) {
        modelValue = value[0];
      }
    } else {
      modelValue = value;
      this.updateCdkConnectedOverlayPositions();
    }
    this.updateNgModel(value, modelValue);
  }

  onSearch(value: string, emit: boolean): void {
    if (emit && (this.searchValue !== value)) {
      this.jdOnSearch.emit(value);
      this.searchValue = value;
    }
  }

  clearNgModel(): void {
    if (this.isSingleMode) {
      this.updateNgModel([], null);
    } else {
      this.updateNgModel([], []);
    }
  }

  // tslint:disable-next-line:no-any
  updateNgModel(list: any[], value: string | string[]): void {
    this.listOfSelectedValue = list;
    if (value !== this.value) {
      this.value = value;
      if (isString(value) && value.indexOf('~') > -1) {
        value = value.split('~')[0];
      }
      this.onChange(value);
    }
  }

  listOfTemplateOptionChange(value: JdOptionComponent[]): void {
    this.listOfTemplateOption = value;
  }

  updateDropDownClassMap(): void {
    this.dropDownClassMap = {
      ['ant-select-dropdown']: true,
      [`ant-select-dropdown--single`]: this.isSingleMode,
      [`ant-select-dropdown--multiple`]: this.isMultipleOrTags,
      [`ant-select-dropdown-placement-bottomLeft`]: this.dropDownPosition === 'bottom',
      [`ant-select-dropdown-placement-topLeft`]: this.dropDownPosition === 'top',
      [`${this.jdDropdownClassName}`]: !!this.jdDropdownClassName
    };
  }

  onClearSelection(e: MouseEvent): void {
    // TODO: should not clear disabled option ?
    e.stopPropagation();
    this.clearNgModel();
  }

  clearSearchValue(): void {
    if (this.isSingleMode) {
      this.jdSelectTopControlComponent.setInputValue('', false);
    } else {
      this.jdSelectTopControlComponent.setInputValue('', false);
    }
  }

  constructor(private renderer: Renderer2) {
    this.notSearchResult$.subscribe(value1 => {
      this.OnNotSearchResult.emit(value1);
    });
  }

  /** update ngModel -> update listOfSelectedValue **/
  // tslint:disable-next-line:no-any
  writeValue(value: any | any[]): void {
    this.value = value;
    if (isNotNil(value)) {
      if (Array.isArray(value)) {
        this.listOfSelectedValue = value;
      } else {
        this.listOfSelectedValue = [value];
      }
    } else {
      this.listOfSelectedValue = [];
    }
  }

  registerOnChange(fn: (value: string | string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.jdDisabled = isDisabled;
  }

  ngOnInit(): void {
    this.isDestroy = false;
    this.updateDropDownClassMap();
  }

  ngAfterViewInit(): void {
    this.isInit = true;
    Promise.resolve().then(() => this.updateCdkConnectedOverlayStatus());
  }

  ngOnDestroy(): void {
    this.isDestroy = true;
  }
}
